/**
 * FooterConfigBlock schema.
 * Defines the sidebar form for the footer configuration block.
 */

/**
 * A column item — either a link or a rich text block.
 * The type field controls which fields are shown via condition.
 */
export const columnItemSchema = {
  title: 'Column Item',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['type', 'label', 'href', 'body'],
    },
  ],
  properties: {
    type: {
      title: 'Item type',
      type: 'string',
      choices: [
        ['link', 'Link'],
        ['text', 'Text block'],
      ],
      default: 'link',
    },
    label: {
      title: 'Label',
      type: 'string',
      condition: (formData) => !formData.type || formData.type === 'link',
    },
    href: {
      title: 'URL',
      widget: 'url',
      description:
        'Use the navigator to find internal pages or paste an external URL',
      condition: (formData) => !formData.type || formData.type === 'link',
    },
    body: {
      title: 'Text',
      type: 'string',
      widget: 'richtext',
      description: 'Rich text — headings, paragraphs, and links are supported',
      condition: (formData) => formData.type === 'text',
    },
  },
  required: [],
};

export const columnSchema = {
  title: 'Column',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['heading', 'width', 'links'],
    },
  ],
  properties: {
    heading: {
      title: 'Heading',
      type: 'string',
    },
    width: {
      title: 'Column width',
      type: 'string',
      choices: [
        ['narrow', 'Narrow'],
        ['normal', 'Normal'],
        ['wide', 'Wide'],
        ['extrawide', 'Extra wide'],
      ],
      description: 'Relative width — leave empty for equal columns',
    },
    links: {
      title: 'Items',
      type: 'array',
      schema: columnItemSchema,
      widget: 'object_list',
      description: 'Add links or text blocks — mix and order them freely',
    },
  },
  required: [],
};

export const socialLinkSchema = {
  title: 'Social Link',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['platform', 'href'],
    },
  ],
  properties: {
    platform: {
      title: 'Platform',
      type: 'string',
      choices: [
        ['linkedin', 'LinkedIn'],
        ['facebook', 'Facebook'],
        ['bluesky', 'Bluesky'],
        ['youtube', 'YouTube'],
        ['mastodon', 'Mastodon'],
        ['twitter', 'X (Twitter)'],
        ['instagram', 'Instagram'],
        ['github', 'GitHub'],
      ],
    },
    href: {
      title: 'URL',
      widget: 'url',
      description: 'Full URL for external social profile',
    },
  },
  required: ['platform', 'href'],
};

export const footerBarLinkSchema = {
  title: 'Footer Bar Link',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['label', 'href'],
    },
  ],
  properties: {
    label: {
      title: 'Label',
      type: 'string',
    },
    href: {
      title: 'URL',
      widget: 'url',
      description:
        'Use the navigator to find internal pages or paste an external URL',
    },
  },
  required: ['label', 'href'],
};

const FooterConfigSchema = {
  title: 'Footer Configuration',
  fieldsets: [
    {
      id: 'default',
      title: 'Columns',
      fields: ['columns'],
    },
    {
      id: 'intro',
      title: 'Intro',
      fields: ['showIntro', 'introWidth', 'introLogoType', 'introCustomLogo', 'introText'],
    },
    {
      id: 'copyright',
      title: 'Copyright & Social',
      fields: ['copyrightWidth', 'badgeImage', 'badgeLink', 'copyright', 'social', 'attribution', 'showBackToTop'],
    },
    {
      id: 'footerbar',
      title: 'Footer Bar',
      fields: ['footerBarLinks'],
    },
  ],
  properties: {
    // --- Columns tab ---
    columns: {
      title: 'Link Columns',
      type: 'array',
      schema: columnSchema,
      widget: 'object_list',
      description:
        'Add as many columns as needed. Each column has a heading and a list of items.',
    },

    // --- Intro tab ---
    showIntro: {
      title: 'Show intro column',
      type: 'boolean',
      description:
        'Adds a first column with a logo and optional text — useful for branding.',
      default: false,
    },
    introWidth: {
      title: 'Intro column width',
      type: 'string',
      choices: [
        ['narrow', 'Narrow'],
        ['normal', 'Normal'],
        ['wide', 'Wide'],
        ['extrawide', 'Extra wide'],
      ],
      description: 'Relative width — leave empty for equal columns',
      condition: (formData) => formData.showIntro,
    },
    introLogoType: {
      title: 'Logo',
      type: 'string',
      choices: [
        ['site', 'Use site logo (from Site Setup)'],
        ['custom', 'Use a custom image'],
        ['none', 'No logo'],
      ],
      default: 'site',
      condition: (formData) => formData.showIntro,
    },
    introCustomLogo: {
      title: 'Custom logo',
      widget: 'url',
      description: 'Link to an image asset saved on the site',
      condition: (formData) =>
        formData.showIntro && formData.introLogoType === 'custom',
    },
    introText: {
      title: 'Intro text',
      type: 'string',
      widget: 'richtext',
      description: 'Optional text shown below the logo',
      condition: (formData) => formData.showIntro,
    },

    // --- Copyright & Social tab ---
    badgeImage: {
      title: 'Badge / governing body logo',
      widget: 'url',
      description: 'Optional image shown above social icons — e.g. a governing body logo',
    },
    badgeLink: {
      title: 'Badge link URL',
      widget: 'url',
      description: 'Optional — wraps the badge image in a link',
    },
    copyrightWidth: {
      title: 'Copyright column width',
      type: 'string',
      choices: [
        ['narrow', 'Narrow'],
        ['normal', 'Normal'],
        ['wide', 'Wide'],
        ['extrawide', 'Extra wide'],
      ],
      description: 'Relative width — leave empty for equal columns',
    },
    copyright: {
      title: 'Copyright text',
      type: 'string',
      description:
        'Use {year} to insert the current year automatically. E.g. "© Juizi {year}"',
    },
    social: {
      title: 'Social links',
      type: 'array',
      schema: socialLinkSchema,
      widget: 'object_list',
    },
    attribution: {
      title: 'Attribution / disclaimer',
      type: 'string',
      widget: 'richtext',
      description: 'Optional text with links, e.g. image credits.',
    },
    showBackToTop: {
      title: 'Show back to top button',
      type: 'boolean',
      default: true,
    },

    // --- Footer bar tab ---
    footerBarLinks: {
      title: 'Footer bar links',
      type: 'array',
      schema: footerBarLinkSchema,
      widget: 'object_list',
      description: 'Bottom strip links — terms of use, privacy policy, etc.',
    },
  },
  required: [],
};

export default FooterConfigSchema;
